import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import Logo, { LogoMark } from '@/components/Logo';
import { useAuth } from '@/context/AuthContext';
import { DASH } from '@/constants/testIds';
import {
  LayoutDashboard, Rocket, Users, MessagesSquare, CalendarDays, FileSignature,
  Building2, Sparkles, LogOut, Search, ChevronsLeft, ChevronsRight,
  Briefcase, ShieldCheck, ClipboardList, Activity,
  Wallet, Sparkle, Compass, ScrollText, ShieldAlert, KeyRound, MessageSquare,
  GraduationCap, BookOpen, Code2, FileText, Shield, Network, Lock, Cloud, Database, BookMarked, Palette,
  DollarSign, CreditCard, MonitorPlay,
  User as UserIcon, Settings, Camera, ChevronDown, ShieldCheck as ShieldCheckIcon,
} from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import ThemeToggle from '@/components/ThemeToggle';
import { MakerStatusPill } from '@/components/RoleImpersonator';
import AITeamAgeBadge from '@/components/AITeamAgeBadge';
import SahibCompanion from '@/components/SahibCompanion';
import MeetingReminderBell from '@/components/MeetingReminderBell';
import WelcomeBadge from '@/components/WelcomeBadge';
import PlanChip from '@/components/PlanChip';
import DataPathBadge from '@/components/DataPathBadge';
import { t as tr } from '@/lib/i18nNav';
import { usePermissions, P } from '@/lib/permissions';
import SandboxRibbon from '@/components/SandboxRibbon';
import MakerControls from '@/components/MakerControls';
import MobileBottomNav from '@/components/MobileBottomNav';
import useNavBadges from '@/hooks/useNavBadges';

// Sectioned navigation — items grouped into labelled buckets so 30+ links
// don't read as one undifferentiated list. Sections render with a small
// uppercase eyebrow when the sidebar is expanded; in rail (collapsed) mode
// only the icons are visible and section dividers become thin gold rules.
function DashSearchForm() {
  const [q, setQ] = useState('');
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    nav(`/dashboard/search?q=${encodeURIComponent(term)}`);
  };
  // Cmd-K / Ctrl-K focus shortcut — focuses the input from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        document.querySelector('[data-testid="dash-search"]')?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <form
      onSubmit={submit}
      className="flex items-center gap-2 max-w-md w-full"
      role="search"
      data-testid="dash-search-form"
    >
      <Search size={16} className="text-slate-400 shrink-0"/>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={tr('header.search')}
        className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none placeholder:text-slate-400 dark:text-white"
        data-testid="dash-search"
      />
      <kbd className="hidden md:inline shrink-0 text-[10px] mono px-1.5 py-0.5 bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-white/60 rounded">⌘ K</kbd>
      <button
        type="submit"
        disabled={!q.trim()}
        data-testid="dash-search-go"
        className="shrink-0 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-bold rounded border border-[var(--brand-primary,#8B3FC5)]/40 text-[var(--brand-primary,#8B3FC5)] hover:bg-[var(--brand-primary,#8B3FC5)]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Go
      </button>
    </form>
  );
}

const NAV_SECTIONS = [
  {
    key: 'workspace',
    label: 'Workspace',
    items: [
      { to: '/dashboard',                    labelKey: 'nav.overview',         icon: LayoutDashboard, testid: DASH.navOverview, end: true, perm: P.VIEW_OVERVIEW },
      { to: '/dashboard/shura',              labelKey: 'nav.shura',            icon: Network,         testid: 'nav-shura',             perm: P.VIEW_OVERVIEW, label: 'Shura Nexus' },
      { to: '/dashboard/insights',           labelKey: 'nav.insights',         icon: Sparkles,        testid: 'nav-insights',          perm: P.VIEW_OVERVIEW, label: 'Ecosystem Insights' },
      { to: '/dashboard/founder',            labelKey: 'nav.founder',          icon: Compass,         testid: 'nav-founder',           perm: P.VIEW_FOUNDER_JOURNEY, founderOnly: true },
    ],
  },
  {
    key: 'amwal',
    label: 'Amwāl OS',
    items: [
      { to: '/dashboard/entities-live',      labelKey: 'nav.entities_live',    icon: Activity,        testid: 'nav-entities-live',     perm: P.VIEW_ENTITIES_LIVE },
      { to: '/dashboard/entities-os',        labelKey: 'nav.entities_os',      icon: Building2,       testid: 'nav-entities-os',       perm: P.VIEW_ENTITIES },
      { to: '/dashboard/finance',            labelKey: 'nav.finance',          icon: Wallet,          testid: 'nav-finance',           perm: P.VIEW_FINANCE, label: 'Financial Engine' },
      { to: '/dashboard/erm',                labelKey: 'nav.erm',              icon: ShieldAlert,     testid: 'nav-erm',               perm: P.VIEW_ERM },
      { to: '/dashboard/opportunities',      labelKey: 'nav.opportunities',    icon: Briefcase,       testid: 'nav-opportunities',     perm: P.VIEW_OPPORTUNITIES, badgeKey: 'opportunities_stuck' },
      { to: '/dashboard/approvals',          labelKey: 'nav.approvals',        icon: ShieldCheck,     testid: 'nav-approvals',         perm: P.VIEW_APPROVALS, badgeKey: 'approvals_pending' },
      { to: '/dashboard/tasks',              labelKey: 'nav.tasks',            icon: ClipboardList,   testid: 'nav-tasks',             perm: P.VIEW_OVERVIEW, label: 'Tasks', badgeKey: 'tasks_open', urgentKey: 'tasks_overdue' },
    ],
  },
  {
    key: 'founders',
    label: 'Founders & Ventures',
    items: [
      { to: '/dashboard/founders',           labelKey: 'nav.founders',         icon: Rocket,          testid: 'nav-founders',          perm: P.MANAGE_USERS, label: 'Founders' },
      { to: '/dashboard/startups',           labelKey: 'nav.startups',         icon: Rocket,          testid: DASH.navStartups,        perm: P.VIEW_STARTUPS },
      { to: '/dashboard/investors',          labelKey: 'nav.investors',        icon: Users,           testid: DASH.navInvestors,       perm: P.VIEW_INVESTORS },
      { to: '/dashboard/agreements',         labelKey: 'nav.agreements',       icon: FileSignature,   testid: DASH.navAgreements,      perm: P.VIEW_AGREEMENTS },
      { to: '/dashboard/chat',               labelKey: 'nav.chat',             icon: MessagesSquare,  testid: DASH.navChat,            perm: P.VIEW_CHAT, badgeKey: 'chat_unread' },
    ],
  },
  {
    key: 'comms',
    label: 'Comms & Calendar',
    items: [
      { to: '/dashboard/content-studio',     labelKey: 'nav.content_studio',   icon: MessageSquare,   testid: 'nav-content-studio',    perm: P.VIEW_APPROVALS, label: 'Content Studio' },
      { to: '/dashboard/integrations',       labelKey: 'nav.integrations',     icon: KeyRound,        testid: 'nav-integrations',      perm: P.VIEW_APPROVALS, label: 'Integrations' },
      { to: '/dashboard/meetings',           labelKey: 'nav.meetings',         icon: CalendarDays,    testid: DASH.navMeetings,        perm: P.VIEW_MEETINGS },
      { to: '/dashboard/minutes',            labelKey: 'nav.minutes',          icon: ClipboardList,   testid: 'nav-minutes',           perm: P.VIEW_MINUTES },
      { to: '/dashboard/events',             labelKey: 'nav.events',           icon: Sparkles,        testid: DASH.navEvents,          perm: P.VIEW_EVENTS },
      { to: '/dashboard/documents',          labelKey: 'nav.documents',        icon: FileText,        testid: 'nav-documents',         perm: P.VIEW_DOCUMENTS },
    ],
  },
  {
    key: 'ai',
    label: 'AI Team',
    items: [
      { to: '/dashboard/ai-employees',       labelKey: 'nav.ai_employees',     icon: Sparkles,        testid: 'nav-ai-employees',      perm: P.VIEW_AI_EMPLOYEES },
      { to: '/dashboard/ai-council',         labelKey: 'nav.ai_council',       icon: MessageSquare,   testid: 'nav-ai-council',        perm: P.VIEW_AI_COUNCIL },
      { to: '/dashboard/ai-drafts',          labelKey: 'nav.ai_drafts',        icon: FileSignature,   testid: 'nav-ai-drafts',         perm: P.VIEW_AI_DRAFTS },
      { to: '/dashboard/ai-mentors',         labelKey: 'nav.ai_mentors',       icon: GraduationCap,   testid: 'nav-ai-mentors',        perm: P.VIEW_AI_MENTORS },
      { to: '/dashboard/library',            labelKey: 'nav.library',          icon: BookOpen,        testid: 'nav-library',           perm: P.VIEW_AI_MENTORS, label: 'Strategic Library' },
    ],
  },
  {
    key: 'reference',
    label: 'Reference',
    items: [
      { to: '/dashboard/help',               labelKey: 'nav.help',             icon: BookOpen,        testid: 'nav-help',              perm: P.VIEW_HELP },
      { to: '/dashboard/manual',             labelKey: 'nav.manual',           icon: BookOpen,        testid: 'nav-manual',            perm: P.VIEW_TENANT_SETTINGS, label: 'Operating Manual' },
      { to: '/dashboard/api-docs',           labelKey: 'nav.api_docs',         icon: Code2,           testid: 'nav-api-docs',          perm: P.VIEW_API_DOCS },
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    items: [
      { to: '/dashboard/audit',              labelKey: 'nav.audit',            icon: ScrollText,      testid: 'nav-audit',             perm: P.VIEW_AUDIT },
      { to: '/dashboard/audit/cloaked-writes', labelKey: 'nav.cloaked_writes', icon: ShieldCheck,     testid: 'nav-cloaked-writes',    perm: P.VIEW_AUDIT, label: 'Cloaked Writes', systemAdminOnly: true },
      { to: '/dashboard/admin/tenants',        labelKey: 'nav.tenants_admin',   icon: Building2,       testid: 'nav-tenants-admin',     perm: P.VIEW_AUDIT, label: 'Tenants', systemAdminOnly: true },
      { to: '/dashboard/admin/forms',          labelKey: 'nav.form_designer',   icon: Settings,        testid: 'nav-form-designer',     perm: P.MANAGE_TENANT_SETTINGS, label: 'Form Designer' },
      { to: '/dashboard/admin/access-requests', labelKey: 'nav.access_requests', icon: ShieldCheck,    testid: 'nav-access-requests',   perm: P.MANAGE_USERS, label: 'Access Requests', badgeKey: 'pending_access' },
      { to: '/dashboard/feedback-journal',   labelKey: 'nav.change_requests',  icon: MessagesSquare,  testid: 'nav-feedback-journal',  perm: P.VIEW_CHANGE_REQUESTS },
      { to: '/dashboard/security',           labelKey: 'nav.security',         icon: KeyRound,        testid: 'nav-security',          perm: P.VIEW_SECURITY },
      { to: '/dashboard/admin/security',     labelKey: 'nav.field_encryption', icon: Lock,            testid: 'nav-admin-security',    perm: P.VIEW_SECURITY, label: 'Field Encryption' },
      { to: '/dashboard/admin/pricing',      labelKey: 'nav.admin_pricing',    icon: DollarSign,      testid: 'nav-admin-pricing',     perm: P.VIEW_SECURITY, label: 'Pricing · Monetization', makerOnly: true },
      { to: '/dashboard/admin/screensaver',  labelKey: 'nav.admin_screensaver', icon: MonitorPlay,    testid: 'nav-admin-screensaver', perm: P.VIEW_SECURITY, label: 'Screensaver' },
      { to: '/dashboard/privacy',            labelKey: 'nav.privacy',          icon: ShieldCheck,     testid: 'nav-privacy',           perm: P.VIEW_SECURITY, label: 'Sovereign AI · Privacy' },
      { to: '/dashboard/admin/aws',          labelKey: 'nav.admin_aws',        icon: Cloud,           testid: 'nav-admin-aws',         perm: P.VIEW_SECURITY, label: 'AWS · Platform', systemAdminOnly: true },
      { to: '/dashboard/admin/azure',        labelKey: 'nav.admin_azure',      icon: Database,        testid: 'nav-admin-azure',       perm: P.VIEW_SECURITY, label: 'Azure · Platform', systemAdminOnly: true },
      { to: '/dashboard/gov/audit',          labelKey: 'nav.gov_audit',        icon: ShieldCheck,     testid: 'nav-gov-audit',         perm: P.VIEW_SECURITY, label: 'Government · Cross-Tenant Audit', systemAdminOnly: true },
      { to: '/dashboard/docs',               labelKey: 'nav.architecture_docs',icon: BookMarked,      testid: 'nav-architecture-docs', perm: P.VIEW_SECURITY, label: 'Architecture Docs' },
      { to: '/dashboard/tenant',             labelKey: 'nav.tenant',           icon: Sparkle,         testid: 'nav-tenant',            perm: P.VIEW_TENANT_SETTINGS },
      { to: '/dashboard/branding',           labelKey: 'nav.branding',         icon: Palette,         testid: 'nav-branding',          perm: P.VIEW_TENANT_SETTINGS, label: 'Branding & Theme' },
      { to: '/dashboard/settings/aws',       labelKey: 'nav.tenant_aws',       icon: Cloud,           testid: 'nav-tenant-aws',        perm: P.VIEW_TENANT_SETTINGS, label: 'AWS · Tenant' },
      { to: '/dashboard/settings/azure',     labelKey: 'nav.tenant_azure',     icon: Database,        testid: 'nav-tenant-azure',      perm: P.VIEW_TENANT_SETTINGS, label: 'Azure · Tenant' },
      { to: '/dashboard/billing/bank',       labelKey: 'nav.billing',          icon: Wallet,          testid: 'nav-bank',              perm: P.VIEW_BANK_BILLING },
      { to: '/dashboard/users',              labelKey: 'nav.users',            icon: Users,           testid: DASH.navUsers,           perm: P.MANAGE_USERS },
      { to: '/dashboard/subscriptions',      labelKey: 'nav.subscriptions',    icon: CreditCard,      testid: 'nav-subscriptions',     perm: P.MANAGE_USERS, label: 'Subscriptions' },
      { to: '/dashboard/roles',              labelKey: 'nav.roles',            icon: Shield,          testid: 'nav-roles',             perm: P.MANAGE_ROLES, label: 'Role Builder' },
      { to: '/dashboard/sandbox',            labelKey: 'nav.sandbox',          icon: Sparkles,        testid: 'nav-sandbox',           perm: P.MANAGE_USERS, label: 'Sandbox · Seed & Reset' },
    ],
  },
];

// Flat list (kept for backwards-compatibility with any consumer that still
// imports NAV directly).  Rebuilt from the sectioned config.
const NAV = NAV_SECTIONS.flatMap((s) => s.items);

export default function DashboardLayout({ children }) {
  const { user, logout, effectiveRole, realRole, isImpersonating } = useAuth();
  const { can, isWhitelabel, loaded: permsLoaded } = usePermissions();
  const nav = useNavigate();
  const location = useLocation();
  // Sidebar is a "rail" by default — narrow (icon-only), and expands on
  // hover. The user can pin it open with the chevron button — that
  // preference is saved to localStorage and restored across reloads.
  const [pinned, setPinned] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('ve_sidebar_pinned') === '1';
  });
  const [hover, setHover] = useState(false);
  const expanded = pinned || hover;
  // Live nav-badge counts — single source of truth shared with
  // MobileBottomNav so desktop + mobile never drift apart.
  const badges = useNavBadges();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('ve_sidebar_pinned', pinned ? '1' : '0');
  }, [pinned]);

  // Role driving the UI — usually equal to user.role, but a system_admin can
  // temporarily preview the app as a different role via the impersonator.
  const viewRole = effectiveRole || user?.role;

  const doLogout = async () => {
    await logout();
    nav('/login');
  };

  const visibleNav = NAV.filter((n) => {
    // Founder-only items are hidden from non-founder roles
    if (n.founderOnly && !['founder', 'startup_member', 'guest'].includes(viewRole)) return false;
    // System-admin-only items
    if (n.systemAdminOnly && viewRole !== 'system_admin') return false;
    // Maker-only items — gated by the is_maker flag from /auth/me
    if (n.makerOnly && !user?.is_maker) return false;
    // Until permissions load, fall back to a permissive (auth-only) view to
    // avoid the navbar flashing empty on slow networks.
    if (!permsLoaded) return true;
    // Permission gate — primary source of truth, mirrors backend
    return !n.perm || can(n.perm);
  });
  const visibleSections = NAV_SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((n) => visibleNav.some((v) => v.to === n.to)),
  })).filter((s) => s.items.length > 0);

  // ── Collapsible nav sections ──
  // Each section can be collapsed to just its header. State persisted
  // in localStorage under `ve_nav_collapsed` (Set encoded as JSON array).
  // Default: ALL sections collapsed — the user explicitly asked for a
  // maximally-short menu on first paint. Auto-expand still kicks in for
  // the section containing the active route, so the user never loses
  // their bearings.
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const raw = localStorage.getItem('ve_nav_collapsed');
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* swallow */ }
    return new Set(NAV_SECTIONS.map((s) => s.key));
  });

  // Auto-expand whichever section contains the current route — keeps the
  // active link visible even when the user collapses everything.
  useEffect(() => {
    const activeSection = visibleSections.find((s) =>
      s.items.some((n) => location.pathname === n.to || (n.to !== '/dashboard' && location.pathname.startsWith(`${n.to}/`)))
    );
    if (activeSection && collapsedSections.has(activeSection.key)) {
      queueMicrotask(() => {
        setCollapsedSections((prev) => {
          const next = new Set(prev);
          next.delete(activeSection.key);
          return next;
        });
      });
    }
  }, [location.pathname]);

  const persistCollapsed = (next) => {
    try { localStorage.setItem('ve_nav_collapsed', JSON.stringify([...next])); } catch { /* swallow */ }
  };
  const toggleSection = (key) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      persistCollapsed(next);
      return next;
    });
  };
  const allCollapsed = collapsedSections.size >= visibleSections.length;
  const toggleAllSections = () => {
    setCollapsedSections(() => {
      const next = allCollapsed ? new Set() : new Set(NAV_SECTIONS.map((s) => s.key));
      persistCollapsed(next);
      return next;
    });
  };

  // Auto-scroll when the mouse hovers near the top/bottom of the nav list.
  // 36px hot-zones above and below trigger a smooth scroll until the cursor
  // leaves the zone. Avoids the trap of a long nav list cutting off below
  // the viewport when the sidebar is sticky.
  const navRef = React.useRef(null);
  const autoScrollRef = React.useRef({ dir: 0, raf: null });
  const onNavMouseMove = (e) => {
    const el = navRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const HOT = 48;
    const top = e.clientY - rect.top;
    const bot = rect.height - top;
    let dir = 0;
    if (top < HOT && el.scrollTop > 0) dir = -1;
    else if (bot < HOT && el.scrollTop + el.clientHeight < el.scrollHeight) dir = 1;
    autoScrollRef.current.dir = dir;
    if (dir !== 0 && !autoScrollRef.current.raf) {
      const tick = () => {
        const d = autoScrollRef.current.dir;
        if (d === 0 || !navRef.current) {
          autoScrollRef.current.raf = null;
          return;
        }
        // Pixels-per-frame, scaled by proximity to edge for natural feel.
        const proximity = d < 0
          ? Math.max(0, HOT - (e.clientY - navRef.current.getBoundingClientRect().top))
          : Math.max(0, HOT - (navRef.current.getBoundingClientRect().bottom - e.clientY));
        const speed = 2 + Math.floor(proximity / 6); // 2..10 px / frame
        navRef.current.scrollTop += d * speed;
        autoScrollRef.current.raf = requestAnimationFrame(tick);
      };
      autoScrollRef.current.raf = requestAnimationFrame(tick);
    }
  };
  const onNavMouseLeave = () => {
    autoScrollRef.current.dir = 0;
    if (autoScrollRef.current.raf) {
      cancelAnimationFrame(autoScrollRef.current.raf);
      autoScrollRef.current.raf = null;
    }
  };
  React.useEffect(() => () => {
    if (autoScrollRef.current.raf) cancelAnimationFrame(autoScrollRef.current.raf);
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5F4] dark:bg-[#0A0A0A] text-slate-900 dark:text-white/90 flex flex-col">
      {/* Cloak warning banner is now hoisted to AppRouter so it shows
          even on Access-restricted pages — see App.js. */}
      <div className="flex flex-1 min-h-0">
      {/* Sidebar — icon-only "rail" by default, expands on hover or when pinned.
          FIXED positioning so the menu is *always* glued to the viewport
          regardless of how far the user scrolls — sticky was failing for
          users on certain pages because the parent flex chain didn't
          provide a tall-enough containing block. We pair the fixed aside
          with a sibling spacer div that reserves the equivalent layout
          width, so the main content never shifts under the rail.
          Long nav lists still auto-scroll on top/bottom edge hover. */}
      <div
        aria-hidden="true"
        data-testid="sidebar-spacer"
        className={`${expanded ? 'w-[280px]' : 'w-[68px]'} shrink-0 transition-[width] duration-300 ease-out`}
      />
      <aside
        data-testid={DASH.sidebar}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => { setHover(false); onNavMouseLeave(); }}
        className={`${expanded ? 'w-[280px]' : 'w-[68px]'} shrink-0 transition-[width] duration-300 ease-out
                    bg-white dark:bg-[#0d0d0d] border-r border-slate-200 dark:border-white/10
                    flex flex-col overflow-hidden
                    fixed top-0 left-0 h-screen z-40`}
      >
        <div className="h-16 flex items-center justify-between px-3 border-b border-slate-200 dark:border-white/10 shrink-0">
          <div className="shrink-0">
            {expanded ? <Logo size="sm" to="/dashboard"/> : <LogoMark className="h-8 w-8" to="/dashboard" />}
          </div>
          {expanded && (
            <button
              className="text-slate-400 hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] transition-colors p-1.5 rounded hover:bg-[#FFF8E5]/50 dark:hover:bg-[#3a2f0a]/50"
              onClick={() => setPinned((p) => !p)}
              aria-label={pinned ? 'Auto-collapse sidebar' : 'Pin sidebar open'}
              data-testid="sidebar-toggle"
              title={pinned ? 'Auto-collapse' : 'Pin open'}
            >
              {pinned ? <ChevronsLeft size={16}/> : <ChevronsRight size={16}/>}
            </button>
          )}
        </div>

        {/* v166 — Welcome notice from the admin who approved this user.
            Self-gates: renders nothing once dismissed or if never set. */}
        {expanded && (
          <div className="px-3 pt-2">
            <WelcomeBadge />
          </div>
        )}

        <nav
          ref={navRef}
          onMouseMove={onNavMouseMove}
          onMouseLeave={onNavMouseLeave}
          className="flex-1 overflow-y-auto overflow-x-hidden p-2 nav-scroll"
          data-testid="dash-nav-scroll"
        >
          {/* Master collapse / expand toggle — only renders in expanded
              sidebar mode. Lets the user nuke the entire menu in one
              click, or restore everything when they're hunting. */}
          {expanded && visibleSections.length > 1 && (
            <button
              type="button"
              onClick={toggleAllSections}
              data-testid="nav-master-toggle"
              className="w-full mb-2 px-3 py-1.5 rounded text-[9px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/45 hover:text-slate-700 dark:hover:text-white/85 hover:bg-slate-100/60 dark:hover:bg-white/[0.04] transition-colors inline-flex items-center justify-between gap-2"
            >
              <span>{allCollapsed ? 'Expand all' : 'Collapse all'}</span>
              <span className="opacity-60">
                {allCollapsed ? '▼' : '◀'}
              </span>
            </button>
          )}
          {visibleSections.map((section, sIdx) => {
            const isCollapsed = expanded && collapsedSections.has(section.key);
            return (
            <div
              key={section.key}
              className={sIdx === 0 ? '' : 'mt-3 pt-3 border-t border-slate-100 dark:border-white/5'}
              data-testid={`nav-section-${section.key}`}
            >
              {expanded ? (
                <button
                  type="button"
                  onClick={() => toggleSection(section.key)}
                  data-testid={`nav-section-toggle-${section.key}`}
                  aria-expanded={!isCollapsed}
                  className="w-full flex items-center justify-between gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 dark:text-white/40 select-none hover:text-slate-600 dark:hover:text-white/70 transition-colors"
                >
                  <span data-testid={`nav-section-label-${section.key}`}>
                    {section.label}
                  </span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <span className="opacity-50 text-[9px] tabular-nums">{section.items.length}</span>
                    <ChevronDown size={12} className={`transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                  </span>
                </button>
              ) : null}
              {/* Smooth height transition via CSS grid-rows trick.
                  `grid-template-rows: 0fr` → `1fr` IS animatable, unlike
                  `height: auto`. The child must use `min-height: 0` +
                  `overflow: hidden` so it actually collapses. */}
              <div
                className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                  isCollapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
                }`}
                aria-hidden={isCollapsed}
              >
                <div className="overflow-hidden min-h-0">
                  <div className="space-y-0.5 pt-0.5">
                    {section.items.filter((n) => visibleNav.some((v) => v.to === n.to)).map((n) => {
                      const badgeCount = n.badgeKey ? (badges[n.badgeKey] || 0) : 0;
                      const isUrgent = n.urgentKey ? (badges[n.urgentKey] || 0) > 0 : false;
                      return (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        data-testid={n.testid}
                        tabIndex={isCollapsed ? -1 : 0}
                        className={({ isActive }) => `ve-side-link relative ${isActive ? 'active' : ''} ${!expanded ? 'justify-center px-2' : ''}`}
                        title={n.label || tr(n.labelKey)}
                      >
                        <n.icon size={17} className="shrink-0" />
                        {expanded ? (
                          <span className="flex-1 flex items-center justify-between gap-2 whitespace-nowrap overflow-hidden">
                            <span className="truncate">{n.label || tr(n.labelKey)}</span>
                            {badgeCount > 0 && (
                              <span
                                data-testid={`nav-badge-${n.testid}`}
                                className={`text-[9px] font-bold px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center rounded-full text-white shrink-0 ${
                                  isUrgent
                                    ? 'bg-rose-500 animate-pulse'
                                    : 'bg-[var(--ve-violet-bright,#8B3FC5)]'
                                }`}
                              >
                                {badgeCount > 99 ? '99+' : badgeCount}
                              </span>
                            )}
                          </span>
                        ) : (
                          badgeCount > 0 && (
                            <span
                              data-testid={`nav-pip-${n.testid}`}
                              className={`absolute right-1 top-1 h-2 w-2 rounded-full ${
                                isUrgent ? 'bg-rose-500 animate-pulse' : 'bg-[var(--ve-violet-bright,#8B3FC5)]'
                              }`}
                            />
                          )
                        )}
                      </NavLink>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </nav>

        <div className="border-t border-slate-200 dark:border-white/10 p-2 shrink-0">
          {expanded && user && (
            <div className="mb-2 px-2">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-white/45">{tr('header.signed_in')}</div>
              <div className="text-sm font-medium mt-0.5 truncate" title={user.name}>{user.name}</div>
              <div className="text-xs text-slate-500 dark:text-white/55 capitalize truncate">
                {viewRole?.replace(/_/g, ' ')}
                {isImpersonating && (
                  <span className="ml-1.5 inline-block text-[9px] font-bold uppercase tracking-[0.18em] text-[#5E1F8B] dark:text-[#8B3FC5] align-middle">
                    {tr('header.preview')}
                  </span>
                )}
              </div>
              {isImpersonating && (
                <div className="text-[10px] text-slate-400 dark:text-white/45 mt-0.5 truncate">
                  {tr('header.real')}: <span className="font-bold">{realRole?.replace(/_/g, ' ')}</span>
                </div>
              )}
            </div>
          )}
          {expanded && user && <PtoToggle currentUser={user}/>}
          <button
            onClick={doLogout}
            data-testid={DASH.logoutBtn}
            className={`ve-side-link w-full ${!expanded ? 'justify-center px-2' : ''}`}
            title={tr('btn.sign_out')}
          >
            <LogOut size={16}/>
            {expanded && <span className="whitespace-nowrap">{tr('btn.sign_out')}</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white dark:bg-[#0d0d0d] border-b border-slate-200 dark:border-white/10 flex items-center justify-between gap-4 px-4 sm:px-6 sticky top-0 z-30">
          {/* Left cluster — privilege state + global search.
              Privilege pill is the leftmost anchor so the maker always
              knows what state they're in. Search lives in its own
              flex-1 wrapper so it can shrink without crashing into the
              maker pill (separating them into two siblings instead of
              one shared flex-row prevents the v167 overlap bug). */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <MakerStatusPill />
            <div className="hidden xl:flex flex-1 min-w-0 max-w-md overflow-hidden">
              <DashSearchForm />
            </div>
          </div>
          {/* Right cluster — context badges + global controls.
              v167 — heavy trim. VersionBadge moved out of the header
              (the sidebar Logo already shows it under the wordmark).
              DataPathBadge + AITeamAgeBadge pushed to 2xl-only since
              they are trust signals, not interactive controls. The
              standalone <Bell/> decorative button was removed since
              MeetingReminderBell is the functional notifications bell.
              v167.1 — the "View as:" RoleImpersonator was also folded
              into the left-side MAKER pill (same dropdown, half the
              footprint, co-located with the privilege-state signal). */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden lg:flex"><PlanChip /></div>
            <div className="hidden 2xl:flex"><DataPathBadge /></div>
            <div className="hidden 2xl:flex"><AITeamAgeBadge /></div>
            <ThemeToggle compact />
            <div className="hidden md:flex"><LanguageSwitcher /></div>
            <MeetingReminderBell />
            <UserMenu />
          </div>
        </header>

        <SandboxRibbon />
        <MakerControls />

        <main key={location.pathname} className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 pb-24 md:pb-8 ve-page-enter">{children}</main>
      </div>
      </div>

      {/* Mobile-only bottom nav — 5 most-used routes, one tap away */}
      <MobileBottomNav />

      {/* v167.2 — Iqra AI concierge as a global FAB. Mounted at the
          dashboard root so it floats above all routes, matching the
          Intercom/Crisp "always-on copilot" pattern. */}
      <SahibCompanion />
    </div>
  );
}

function UserMenu() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest('[data-testid="user-menu-root"]')) setOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open]);

  const pickAvatar = () => fileInputRef.current?.click();

  const onAvatarSelected = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      const { toast } = await import('sonner');
      toast.error('Picture must be ≤ 2 MB.');
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const { default: api } = await import('@/lib/api');
          const { toast } = await import('sonner');
          await api.put('/auth/me', { avatar_data_url: ev.target.result });
          toast.success('Profile picture updated.');
          // Reload user from context — easiest: page reload
          window.location.reload();
        } catch (err) {
          const { toast } = await import('sonner');
          toast.error(err?.response?.data?.detail || 'Could not save picture.');
        } finally { setUploading(false); }
      };
      reader.readAsDataURL(f);
    } catch {
      setUploading(false);
    }
  };

  const initials = (user?.name || user?.email || 'V').split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join('');
  const avatar = user?.avatar_data_url;

  return (
    <div className="relative" data-testid="user-menu-root">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onAvatarSelected}
        data-testid="user-menu-avatar-file"
      />
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="user-menu-trigger"
        className="hidden md:flex items-center gap-2 pr-2 pl-1 py-1 rounded-full border border-transparent hover:border-[#8B3FC5]/40 transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {avatar ? (
          <img src={avatar} alt="" className="h-8 w-8 rounded-full object-cover border border-[#8B3FC5]/40"/>
        ) : (
          <div className="h-8 w-8 rounded-full bg-[#0A0A0A] text-[#8B3FC5] flex items-center justify-center text-xs font-bold uppercase">
            {initials || 'V'}
          </div>
        )}
        <div className="text-left hidden lg:block">
          <div className="text-xs font-bold leading-tight max-w-[140px] truncate">{user?.name || 'Vigilant'}</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 dark:text-white/45 leading-tight">{user?.role?.replace(/_/g, ' ') || ''}</div>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}/>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 ve-card p-1.5 shadow-2xl z-50"
          data-testid="user-menu-panel"
          role="menu"
        >
          <div className="px-3 py-3 border-b border-slate-200 dark:border-white/10">
            <div className="flex items-center gap-3">
              {avatar ? (
                <img src={avatar} alt="" className="h-10 w-10 rounded-full object-cover border border-[#8B3FC5]/40"/>
              ) : (
                <div className="h-10 w-10 rounded-full bg-[#0A0A0A] text-[#8B3FC5] flex items-center justify-center text-sm font-bold uppercase">
                  {initials || 'V'}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-bold truncate">{user?.name || '—'}</div>
                <div className="text-[11px] text-slate-500 dark:text-white/55 truncate">{user?.email || ''}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#5E1F8B] dark:text-[#8B3FC5] font-bold mt-0.5">
                  {user?.role?.replace(/_/g, ' ') || 'member'}
                </div>
              </div>
            </div>
          </div>

          <MenuRow
            testid="user-menu-upload-avatar"
            icon={Camera}
            label={uploading ? 'Uploading…' : (avatar ? 'Change picture' : 'Upload picture')}
            onClick={() => { setOpen(false); pickAvatar(); }}
            disabled={uploading}
          />
          <MenuRow
            testid="user-menu-profile"
            icon={UserIcon}
            label="Profile"
            onClick={() => { setOpen(false); nav('/dashboard/profile'); }}
          />
          <MenuRow
            testid="user-menu-settings"
            icon={Settings}
            label="Settings"
            onClick={() => { setOpen(false); nav('/dashboard/settings'); }}
          />
          <MenuRow
            testid="user-menu-trusted-devices"
            icon={ShieldCheckIcon}
            label="Trusted Devices"
            onClick={() => { setOpen(false); nav('/dashboard/security/trusted-devices'); }}
          />
          <div className="my-1 border-t border-slate-200 dark:border-white/10"/>
          <MenuRow
            testid="user-menu-logout"
            icon={LogOut}
            label="Sign out"
            tone="danger"
            onClick={() => { setOpen(false); signOut(); }}
          />
        </div>
      )}
    </div>
  );
}

function MenuRow({ icon: Icon, label, onClick, tone, testid, disabled }) {
  const toneCls = tone === 'danger'
    ? 'text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20'
    : 'text-slate-700 dark:text-white/85 hover:bg-[#FFF8E5]/70 dark:hover:bg-[#3a2f0a]/40';
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-3 transition-colors disabled:opacity-50 ${toneCls}`}
      role="menuitem"
    >
      <Icon size={15}/>
      <span className="font-medium">{label}</span>
    </button>
  );
}


// PTO / Out-of-office toggle — pinned in the sidebar above the Sign Out button.
// Lets approvers silence the daily digest email while on leave; in-app pending
// counts are unaffected. Updates via PATCH /api/users/me/pto.
function PtoToggle({ currentUser }) {
  const [pto, setPto] = React.useState(!!currentUser?.pto_mode);
  const [saving, setSaving] = React.useState(false);
  const toggle = async () => {
    const next = !pto;
    setPto(next);          // optimistic
    setSaving(true);
    try {
      const api = (await import('@/lib/api')).default;
      const { toast } = await import('sonner');
      const { t: tr2 } = await import('@/lib/i18nNav');
      await api.patch('/users/me/pto', { pto_mode: next });
      toast.success(next ? tr2('btn.pto_on') : tr2('btn.pto_off'));
    } catch (e) {
      setPto(!next);
      const { toast } = await import('sonner');
      toast.error(e?.response?.data?.detail || 'Could not update PTO');
    } finally { setSaving(false); }
  };
  return (
    <button
      onClick={toggle}
      disabled={saving}
      data-testid="sidebar-pto-toggle"
      className="w-full flex items-center justify-between gap-2 px-3 py-2 mb-2 rounded-md text-xs hover:bg-[#FFF8E5] transition disabled:opacity-50"
      title="Pause daily approver-digest emails while on leave."
    >
      <span className="flex items-center gap-2">
        <span className={`inline-block h-2 w-2 rounded-full ${pto ? 'bg-amber-500' : 'bg-slate-300'}`}/>
        <span className="font-bold uppercase tracking-[0.18em]">{tr('btn.pto')}</span>
      </span>
      <span className={`h-4 w-7 rounded-full relative transition ${pto ? 'bg-[#8B3FC5]' : 'bg-slate-300'}`}>
        <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${pto ? 'left-3' : 'left-0.5'}`}/>
      </span>
    </button>
  );
}
